import { arrayMove } from '@dnd-kit/sortable';

export const getParentSerial = (question) =>
  question?.parentQuestionSerialNumber ?? question?.parentSerialNumber ?? null;

export const isTopLevelQuestion = (question) => {
  const parent = getParentSerial(question);
  return parent == null || parent === '';
};

export const getTopLevelQuestions = (questions) =>
  (questions || []).filter(isTopLevelQuestion);

export const getChildQuestions = (questions, parentSerialNumber) => {
  const parentSn = String(parentSerialNumber);
  return (questions || []).filter(
    (q) => String(getParentSerial(q) ?? '') === parentSn,
  );
};

export const nextTopLevelSerialNumber = (questions) => {
  const tops = getTopLevelQuestions(questions);
  const nums = tops
    .map((q) => parseInt(String(q.serialNumber), 10))
    .filter((n) => !Number.isNaN(n));
  return String((nums.length ? Math.max(...nums) : 0) + 1);
};

const nextChildSuffixLetter = (existingChildren, parentSerialNumber) => {
  const parentSn = String(parentSerialNumber);
  const used = new Set(
    existingChildren.map((c) => {
      const sn = String(c.serialNumber);
      if (!sn.startsWith(parentSn)) return '';
      return sn.slice(parentSn.length);
    }).filter((s) => /^[a-z]$/.test(s)),
  );
  for (let i = 0; i < 26; i += 1) {
    const letter = String.fromCharCode(97 + i);
    if (!used.has(letter)) return letter;
  }
  return 'z';
};

export const nextChildSerialNumber = (questions, parentSerialNumber) => {
  const children = getChildQuestions(questions, parentSerialNumber);
  const suffix = nextChildSuffixLetter(children, parentSerialNumber);
  return `${String(parentSerialNumber)}${suffix}`;
};

export const normalizeQuestionsFromApi = (questions) => {
  if (!Array.isArray(questions) || questions.length === 0) return [];

  const hasFlatParentRefs = questions.some(
    (q) => getParentSerial(q) != null && getParentSerial(q) !== '',
  );
  if (hasFlatParentRefs) {
    return questions.map((q) => ({
      ...q,
      serialNumber: String(q.serialNumber),
      parentQuestionSerialNumber: getParentSerial(q) != null ? String(getParentSerial(q)) : null,
      isMultiPart: Boolean(q.isMultiPart ?? q.isMultipart),
    }));
  }

  const flat = [];
  questions.forEach((q) => {
    const nested =
      q.subQuestions || q.multipartChildren || q.children || [];
    const { subQuestions, multipartChildren, children, ...parent } = q;
    const parentSn = String(parent.serialNumber);
    const isMultiPart =
      Boolean(parent.isMultiPart ?? parent.isMultipart) || nested.length > 0;

    flat.push({
      ...parent,
      serialNumber: parentSn,
      parentQuestionSerialNumber: null,
      isMultiPart,
    });

    nested.forEach((child) => {
      flat.push({
        ...child,
        serialNumber: String(child.serialNumber),
        parentQuestionSerialNumber: parentSn,
        isMultiPart: false,
      });
    });
  });
  return flat;
};

export const renumberTopLevelSerials = (questions) => {
  const tops = getTopLevelQuestions(questions);
  const result = [];

  tops.forEach((parent, idx) => {
    const newParentSn = String(idx + 1);
    const oldParentSn = String(parent.serialNumber);
    const children = getChildQuestions(questions, oldParentSn);

    result.push({
      ...parent,
      serialNumber: newParentSn,
      parentQuestionSerialNumber: null,
      options: (parent.options || []).map((opt) => ({
        ...opt,
        questionSerialNumber: newParentSn,
      })),
    });

    children.forEach((child, childIdx) => {
      const suffix = String.fromCharCode(97 + childIdx);
      const newChildSn = `${newParentSn}${suffix}`;
      result.push({
        ...child,
        serialNumber: newChildSn,
        parentQuestionSerialNumber: newParentSn,
        options: (child.options || []).map((opt) => ({
          ...opt,
          questionSerialNumber: newChildSn,
        })),
      });
    });
  });

  return result;
};

export const reorderTopLevelQuestions = (questions, activeId, overId) => {
  const tops = getTopLevelQuestions(questions);
  const oldIndex = tops.findIndex((q) => String(q.serialNumber) === String(activeId));
  const newIndex = tops.findIndex((q) => String(q.serialNumber) === String(overId));
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return questions;

  const reorderedTops = arrayMove(tops, oldIndex, newIndex);
  const rebuilt = reorderedTops.flatMap((parent) => [
    parent,
    ...getChildQuestions(questions, parent.serialNumber),
  ]);
  return renumberTopLevelSerials(rebuilt);
};

export const reorderChildQuestions = (
  questions,
  parentSerialNumber,
  activeId,
  overId,
) => {
  const parentSn = String(parentSerialNumber);
  const tops = getTopLevelQuestions(questions);
  const parent = tops.find((q) => String(q.serialNumber) === parentSn);
  if (!parent) return questions;

  const children = getChildQuestions(questions, parentSn);
  const oldIndex = children.findIndex((q) => String(q.serialNumber) === String(activeId));
  const newIndex = children.findIndex((q) => String(q.serialNumber) === String(overId));
  if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return questions;

  const reorderedChildren = arrayMove(children, oldIndex, newIndex).map(
    (child, childIdx) => {
      const suffix = String.fromCharCode(97 + childIdx);
      const newChildSn = `${parentSn}${suffix}`;
      return {
        ...child,
        serialNumber: newChildSn,
        parentQuestionSerialNumber: parentSn,
        options: (child.options || []).map((opt) => ({
          ...opt,
          questionSerialNumber: newChildSn,
        })),
      };
    },
  );

  const childIds = new Set(children.map((c) => String(c.serialNumber)));
  const withoutChildren = questions.filter(
    (q) => !childIds.has(String(q.serialNumber)),
  );
  const parentIndex = withoutChildren.findIndex(
    (q) => String(q.serialNumber) === parentSn,
  );
  const next = [...withoutChildren];
  next.splice(parentIndex + 1, 0, ...reorderedChildren);
  return next;
};

export const flattenQuestionsForApi = (questions) => {
  const normalized = normalizeQuestionsFromApi(questions);
  return normalized.map((q) => {
    const {
      subQuestions,
      multipartChildren,
      children,
      isSubQuestion,
      parentSerialNumber,
      isMultiPart,
      ...rest
    } = q;

    const parentQuestionSerialNumber = getParentSerial(q);
    const isMultipart = parentQuestionSerialNumber
      ? false
      : Boolean(isMultiPart ?? q.isMultipart);

    return {
      ...rest,
      serialNumber: String(rest.serialNumber),
      isMultipart: parentQuestionSerialNumber ? false : isMultipart,
      parentQuestionSerialNumber: parentQuestionSerialNumber != null ? String(parentQuestionSerialNumber) : null,
    };
  });
};
